import React, { useEffect, useState, useContext, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { auditActions } from "store/actions";
import { LoginContext } from "helpers/LoginContext";
import { roleAccess } from "helpers/RolesPermission";
import { USER_ROLE, getParticipantLabel, applyParticipantTerminology } from "Constants/constant";
import SideBar from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import DiffView from "components/common/AuditLog/DiffView";
import SnapshotView from "components/common/AuditLog/SnapshotView";
import { HiOutlineEye, HiChevronUp, HiRefresh, HiOutlineBell } from "react-icons/hi";
import { FiDownload, FiSearch } from "react-icons/fi";
import { trackFeatureUse } from "Utils/trackFeatureUse";
import Pagination from "components/common/Pagination";
import { formatDateTimeInClientTimezone } from "Utils/utils";
import auditApi from "store/services/audit.services";
import useCareSector from "hooks/useCareSector";

const NEW_ACTIVITY_POLL_MS = 15000;

const ACTION_BADGE = {
  created: "bg-green-100 text-green-700",
  updated: "bg-blue-100 text-blue-700",
  deleted: "bg-red-100 text-red-700",
  activated: "bg-green-100 text-green-700",
  deactivated: "bg-orange-100 text-orange-700",
  status_changed: "bg-purple-100 text-purple-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  submitted: "bg-blue-100 text-blue-700",
  login: "bg-gray-100 text-gray-700",
  logout: "bg-gray-100 text-gray-700",
  bulk_deleted: "bg-red-100 text-red-700",
  copied: "bg-blue-100 text-blue-700",
  published: "bg-indigo-100 text-indigo-700",
  enabled: "bg-green-100 text-green-700",
  disabled: "bg-orange-100 text-orange-700",
  sent: "bg-blue-100 text-blue-700",
  password_set: "bg-green-100 text-green-700",
  association_removed: "bg-orange-100 text-orange-700",
  viewed: "bg-gray-100 text-gray-700",
  shift_created: "bg-indigo-100 text-indigo-700",
};

const DEFAULT_BADGE = "bg-gray-100 text-gray-700";

const FILTER_CONTROL_CLASS =
  "h-9 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500";

const FILTER_LABEL_CLASS = "block text-xs font-medium text-gray-500 mb-1";

const formatEntityType = (val, careSector = null) => {
  if (!val) return "—";
  if (val === "participant") {
    return getParticipantLabel(careSector, false);
  }
  if (val === "sp_service_category") return "Service Category";
  if (val === "custom_line_item") return "Custom Line Item";
  return val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const toLocalDateString = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getDefaultDateRange = (maxDays = 14) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - maxDays);
  return {
    from: toLocalDateString(from),
    to: toLocalDateString(to),
  };
};

const getDaysBetween = (fromStr, toStr) => {
  if (!fromStr || !toStr) return 0;
  const a = new Date(fromStr);
  const b = new Date(toStr);
  return Math.ceil((b - a) / (1000 * 60 * 60 * 24));
};

const getVal = (obj, key) =>
  obj && (typeof obj.get === "function" ? obj.get(key) : obj[key]);

const ExpandedRow = ({ metadata }) => {
  const meta = metadata || {};

  return (
    <tr>
      <td colSpan={7} className="px-4 py-3 bg-gray-50">
        {meta.changes && <DiffView changes={meta.changes} />}
        {meta.snapshot && (
          <SnapshotView
            snapshot={meta.snapshot}
            relatedCounts={meta.related_counts}
          />
        )}
        {meta.old_status !== undefined && (
          <div className="mt-2 text-xs">
            <span className="font-medium text-gray-500 mr-1">Status:</span>
            <span className="text-red-500 line-through">
              {String(meta.old_status)}
            </span>
            <span className="mx-2">→</span>
            <span className="text-green-600 font-medium">
              {String(meta.new_status)}
            </span>
          </div>
        )}
        {!meta.changes &&
          !meta.snapshot &&
          meta.old_status === undefined && (
            <p className="text-sm text-gray-400 italic">
              No additional details available.
            </p>
          )}
      </td>
    </tr>
  );
};

const AuditLogPage = () => {
  const dispatch = useDispatch();
  const { sideBarPadding } = useContext(LoginContext);
  const { careSector } = useCareSector();

  const loading = useSelector((state) => state.audit?.get("listLoading"));
  const error = useSelector((state) => state.audit?.get("error"));
  const auditLogList = useSelector((state) =>
    state.audit?.get("auditLogList")
  );
  const entityTypes = useSelector((state) => state.audit?.get("entityTypes"));
  const exportLoading = useSelector((state) =>
    state.audit?.get("exportLoading")
  );
  const maxSearchDays = entityTypes?.get?.("max_search_days") ?? entityTypes?.max_search_days ?? 14;

  const [filters, setFilters] = useState(() => ({
    page: 1,
    per_page: 20,
    search: "",
    entity_type: "",
    action: "",
    ...getDefaultDateRange(14),
  }));

  const [expandedRow, setExpandedRow] = useState(null);
  const [dateRangeError, setDateRangeError] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [newActivityCount, setNewActivityCount] = useState(0);
  const lastKnownLatestIdRef = useRef(null);
  const loadingRef = useRef(false);
  const pollFilterParamsRef = useRef(null);
  const checkForNewActivityRef = useRef(() => {});

  useEffect(() => {
    dispatch(auditActions.getAuditLogEntityTypesRequest());
  }, [dispatch]);

  useEffect(() => {
    if (filters.search === "") {
      setDebouncedSearch("");
      return;
    }
    const timeoutId = setTimeout(() => setDebouncedSearch(filters.search), 500);
    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  useEffect(() => {
    if (filters.from && filters.to) {
      const days = getDaysBetween(filters.from, filters.to);
      if (days > maxSearchDays) {
        setDateRangeError(`Date range cannot exceed ${maxSearchDays} days.`);
        return;
      }
      setDateRangeError("");
    }
  }, [filters.from, filters.to, maxSearchDays]);

  const listQueryParams = useMemo(() => {
    if (
      filters.from &&
      filters.to &&
      getDaysBetween(filters.from, filters.to) > maxSearchDays
    ) {
      return null;
    }
    const payload = {
      page: filters.page,
      per_page: filters.per_page,
      from: filters.from,
      to: filters.to,
      entity_type: filters.entity_type,
      action: filters.action,
      search: debouncedSearch,
    };
    const cleaned = {};
    Object.keys(payload).forEach((k) => {
      if (payload[k] !== "" && payload[k] !== null && payload[k] !== undefined) {
        cleaned[k] = payload[k];
      }
    });
    return cleaned;
  }, [
    filters.page,
    filters.per_page,
    filters.from,
    filters.to,
    filters.entity_type,
    filters.action,
    debouncedSearch,
    maxSearchDays,
  ]);

  useEffect(() => {
    if (!listQueryParams) return;
    dispatch(auditActions.getAuditLogListRequest(listQueryParams));
  }, [listQueryParams, dispatch]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const pollFilterKey = useMemo(() => {
    if (!listQueryParams) return null;
    const { page, per_page, ...filterParams } = listQueryParams;
    return JSON.stringify(filterParams);
  }, [listQueryParams]);

  useEffect(() => {
    if (!pollFilterKey) return;
    const { page, per_page, ...filterParams } = listQueryParams;
    pollFilterParamsRef.current = filterParams;
    lastKnownLatestIdRef.current = null;
    setNewActivityCount(0);
  }, [pollFilterKey, listQueryParams]);

  useEffect(() => {
    if (loading || !listQueryParams || filters.page !== 1) return;
    const listData = getVal(auditLogList, "data") || [];
    const firstId = listData[0]?.id;
    if (firstId != null) {
      const previousId = lastKnownLatestIdRef.current;
      lastKnownLatestIdRef.current = firstId;
      if (previousId !== firstId) {
        setNewActivityCount(0);
      }
      checkForNewActivityRef.current();
    }
  }, [loading, auditLogList, filters.page, listQueryParams]);

  useEffect(() => {
    if (!pollFilterKey) return;

    const checkForNewActivity = async () => {
      if (document.hidden || loadingRef.current) return;
      if (lastKnownLatestIdRef.current == null) return;

      const filterParams = pollFilterParamsRef.current;
      if (!filterParams) return;

      try {
        const response = await auditApi.auditLogNewCountRequest({
          ...filterParams,
          since_id: lastKnownLatestIdRef.current,
        });
        const count = Number(response?.new_count) || 0;
        setNewActivityCount(count > 0 ? count : 0);
        return;
      } catch {
        // Fall back to a lightweight list check if new-count is unavailable.
      }

      try {
        const response = await auditApi.auditLogListRequest({
          ...filterParams,
          page: 1,
          per_page: 1,
        });
        const latestId = response?.data?.[0]?.id;
        if (
          latestId != null &&
          Number(latestId) > Number(lastKnownLatestIdRef.current)
        ) {
          setNewActivityCount(1);
        }
      } catch {
        // Ignore background poll errors; main list fetch shows failures.
      }
    };

    checkForNewActivityRef.current = checkForNewActivity;

    const intervalId = setInterval(checkForNewActivity, NEW_ACTIVITY_POLL_MS);
    const onVisibilityChange = () => {
      if (!document.hidden) {
        checkForNewActivity();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [pollFilterKey]);

  const handleFilterChange = (field, value) => {
    setExpandedRow(null);
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field !== "page" && field !== "per_page" ? { page: 1 } : {}),
    }));
    if (field === "from" || field === "to") setDateRangeError("");
  };

  const handleClearFilters = () => {
    setExpandedRow(null);
    setDateRangeError("");
    setFilters({
      page: 1,
      per_page: 20,
      search: "",
      entity_type: "",
      action: "",
      ...getDefaultDateRange(maxSearchDays),
    });
  };

  const handleRefresh = () => {
    if (!listQueryParams || loading) return;
    setExpandedRow(null);
    setNewActivityCount(0);
    trackFeatureUse("audit_log_refresh");
    dispatch(auditActions.getAuditLogListRequest(listQueryParams));
  };

  const handleLoadLatest = useCallback(() => {
    if (!listQueryParams || loading) return;
    setExpandedRow(null);
    setNewActivityCount(0);
    trackFeatureUse("audit_log_refresh_new_activity");
    if (filters.page !== 1) {
      setFilters((prev) => ({ ...prev, page: 1 }));
      return;
    }
    dispatch(auditActions.getAuditLogListRequest(listQueryParams));
  }, [dispatch, filters.page, listQueryParams, loading]);

  const handleExport = () => {
    if (filters.from && filters.to && getDaysBetween(filters.from, filters.to) > maxSearchDays) {
      setDateRangeError(`Date range cannot exceed ${maxSearchDays} days.`);
      return;
    }
    trackFeatureUse("audit_log_export");
    const payload = { ...filters };
    const cleaned = {};
    Object.keys(payload).forEach((k) => {
      if (payload[k] !== "" && payload[k] !== null && payload[k] !== undefined) {
        cleaned[k] = payload[k];
      }
    });
    dispatch(auditActions.exportAuditLogRequest(cleaned));
  };

  const data = getVal(auditLogList, "data") || [];
  const currentPage = getVal(auditLogList, "current_page") || 1;
  const lastPage = getVal(auditLogList, "last_page") || 1;
  const total = getVal(auditLogList, "total") || 0;

  const entityTypeOptions = entityTypes?.get?.("entity_types") ?? entityTypes?.entity_types ?? [];
  const actionOptions = entityTypes?.get?.("actions") ?? entityTypes?.actions ?? [];

  const hasActiveFilters =
    filters.search ||
    filters.entity_type ||
    filters.action ||
    filters.from ||
    filters.to;

  return (
    <div className="min-h-full">
      <SideBar />
      <main>
        <div
          className={`${
            sideBarPadding
              ? "lg:pl-[4rem] duration-300 flex flex-col"
              : "lg:pl-64 duration-300 flex flex-col"
          }`}
        >
          <Header title="Audit Log" desc="System-wide activity audit trail" />

          <div className="w-full">
            {newActivityCount > 0 && (
              <div className="px-4 sm:px-6 lg:px-8 pt-4">
                <div
                  role="status"
                  aria-live="polite"
                  className="flex w-full flex-col gap-3 rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50 px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 items-start gap-3 sm:items-center">
                    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 ring-4 ring-blue-50">
                      <HiOutlineBell className="h-5 w-5" aria-hidden="true" />
                      <span className="absolute -right-0.5 -top-0.5 flex h-3 w-3">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                        <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-500" />
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-blue-900">
                        New activity available
                      </p>
                      <p className="mt-0.5 text-sm text-blue-700">
                        {newActivityCount.toLocaleString()} new{" "}
                        {newActivityCount === 1 ? "entry has" : "entries have"} been
                        logged since you last refreshed.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleLoadLatest}
                    disabled={loading}
                    className="inline-flex shrink-0 items-center justify-center gap-1.5 self-start rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:self-center"
                  >
                    <HiRefresh className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                  </button>
                </div>
              </div>
            )}

            {/* Filters Bar */}
            <div className="bg-white border-b px-4 sm:px-6 lg:px-8 py-4 sticky top-20 z-10">
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 mb-3">
                <p className="text-sm text-gray-500">
                  Search limited to the last {maxSearchDays} days.
                </p>
                {total > 0 && !loading && (
                  <p className="text-xs text-gray-400">
                    {total.toLocaleString()} {total === 1 ? "entry" : "entries"}
                  </p>
                )}
              </div>
              {dateRangeError && (
                <p className="text-sm text-red-600 mb-3">{dateRangeError}</p>
              )}
              {error && (
                <p className="text-sm text-red-600 mb-3">
                  {error?.response?.data?.message || error?.message || String(error)}
                </p>
              )}
              <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
                <div className="grid grid-cols-12 gap-2 items-end">
                  {/* Search */}
                  <div className="col-span-12 md:col-span-4 lg:col-span-2">
                    <label className={FILTER_LABEL_CLASS}>Search</label>
                    <div className="relative">
                      <FiSearch className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Description or user"
                        value={filters.search}
                        onChange={(e) =>
                          handleFilterChange("search", e.target.value)
                        }
                        className={`${FILTER_CONTROL_CLASS} pl-9`}
                      />
                    </div>
                  </div>

                  {/* Entity Type */}
                  <div className="col-span-6 md:col-span-4 lg:col-span-2">
                    <label className={FILTER_LABEL_CLASS}>Entity type</label>
                    <select
                      value={filters.entity_type}
                      onChange={(e) =>
                        handleFilterChange("entity_type", e.target.value)
                      }
                      title={
                        filters.entity_type
                          ? formatEntityType(filters.entity_type, careSector)
                          : "All entity types"
                      }
                      className={FILTER_CONTROL_CLASS}
                    >
                      <option value="">All</option>
                      {entityTypeOptions.map((et) => (
                        <option key={et} value={et}>
                          {formatEntityType(et, careSector)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Action */}
                  <div className="col-span-6 md:col-span-2 lg:col-span-2">
                    <label className={FILTER_LABEL_CLASS}>Action</label>
                    <select
                      value={filters.action}
                      onChange={(e) =>
                        handleFilterChange("action", e.target.value)
                      }
                      className={FILTER_CONTROL_CLASS}
                    >
                      <option value="">All</option>
                      {actionOptions.map((a) => (
                        <option key={a} value={a}>
                          {formatEntityType(a)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date From */}
                  <div className="col-span-6 md:col-span-3 lg:col-span-2">
                    <label className={FILTER_LABEL_CLASS}>From</label>
                    <input
                      type="date"
                      value={filters.from}
                      onChange={(e) =>
                        handleFilterChange("from", e.target.value)
                      }
                      className={FILTER_CONTROL_CLASS}
                    />
                  </div>

                  {/* Date To */}
                  <div className="col-span-6 md:col-span-3 lg:col-span-2">
                    <label className={FILTER_LABEL_CLASS}>To</label>
                    <input
                      type="date"
                      value={filters.to}
                      onChange={(e) =>
                        handleFilterChange("to", e.target.value)
                      }
                      className={FILTER_CONTROL_CLASS}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="col-span-12 lg:col-span-2 flex items-end justify-end gap-1.5">
                    <button
                      type="button"
                      onClick={handleRefresh}
                      disabled={loading || !listQueryParams}
                      title="Refresh audit log"
                      aria-label="Refresh audit log"
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <HiRefresh
                        className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                      />
                    </button>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-800 whitespace-nowrap"
                      >
                        Clear
                      </button>
                    )}
                    {(roleAccess([USER_ROLE.Sp_Admin]) ||
                      roleAccess([], "audit_log", "audit_log.export")) && (
                      <button
                        type="button"
                        onClick={handleExport}
                        disabled={exportLoading}
                        title="Export CSV"
                        aria-label="Export CSV"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        <FiDownload className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Results Table */}
            <div className="bg-white max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
              <div className="flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                    <div className="shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Date/Time
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              User
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Action
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Entity Type
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              Description
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              IP Address
                            </th>
                            <th className="bg-blue-50 px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                              Details
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {loading ? (
                            <tr>
                              <td colSpan={7} className="bg-gray-50 py-6">
                                <CircularLoader
                                  classes="flex justify-center items-center"
                                  classloader="loader-l"
                                />
                              </td>
                            </tr>
                          ) : data.length > 0 ? (
                            data.map((item) => (
                              <React.Fragment key={item.id}>
                                <tr className="even:bg-white odd:bg-gray-50 hover:bg-blue-50/30 transition-colors">
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {formatDateTimeInClientTimezone(item.created_at)}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 capitalize">
                                    {item.user?.name || "System"}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                                    <span
                                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        ACTION_BADGE[item.action] ||
                                        DEFAULT_BADGE
                                      }`}
                                    >
                                      {formatEntityType(item.action)}
                                    </span>
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                    {formatEntityType(item.entity_type, careSector)}
                                  </td>
                                  <td
                                    className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate"
                                    title={item.description}
                                  >
                                    {item.description || "—"}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-400 font-mono">
                                    {item.ip_address || "—"}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                    <button
                                      onClick={() =>
                                        setExpandedRow(
                                          expandedRow === item.id
                                            ? null
                                            : item.id
                                        )
                                      }
                                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                      title="View details"
                                    >
                                      {expandedRow === item.id ? (
                                        <HiChevronUp className="w-5 h-5" />
                                      ) : (
                                        <HiOutlineEye className="w-5 h-5" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                                {expandedRow === item.id && (
                                  <ExpandedRow metadata={item.metadata} />
                                )}
                              </React.Fragment>
                            ))
                          ) : (
                            <tr>
                              <td
                                colSpan={7}
                                className="py-8 px-4 text-center"
                              >
                                <p className="text-gray-500 mb-2">
                                  No audit log entries found.
                                </p>
                                <p className="text-sm text-gray-400 max-w-md mx-auto">
                                  {applyParticipantTerminology(
                                    "Audit entries are created when users create, update, or delete participants, employees, shifts, and other tracked entities. Try a different date range or filters.",
                                    careSector
                                  )}
                                </p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {data.length > 0 && (
              <div className="bg-white px-3 sm:px-6 lg:px-8 max-w-full mx-auto py-4 sm:py-6 border-t border-gray-200">
                <Pagination
                  page={currentPage}
                  activeListLength={data}
                  limit={filters.per_page || 20}
                  totalList={total}
                  totalPages={lastPage}
                  handlePagination={(page) => {
                    setFilters((prev) => ({ ...prev, page }));
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuditLogPage;
